import { Suspense, useRef, useState, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, PresentationControls, Center, Html } from '@react-three/drei';
import { Loader2, RotateCcw, Maximize2 } from 'lucide-react';

function Model({ url, autoRotate }) {
  const { scene } = useGLTF(url);
  const ref = useRef();
  
  useFrame((state) => {
    if (autoRotate && ref.current) {
      ref.current.rotation.y += 0.003;
    }
  });

  return (
    <Center>
      <primitive ref={ref} object={scene} scale={1.5} />
    </Center>
  );
}

function LoadingSpinner() {
  return (
    <Html center>
      <div className="flex flex-col items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
        <span className="text-sm text-zinc-400 mt-2">Loading 3D Model...</span>
      </div>
    </Html>
  );
}

function Scene({ modelUrl, autoRotate }) {
  const { camera } = useThree();
  
  useEffect(() => {
    camera.position.set(0, 0, 5);
  }, [camera]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <spotLight
        position={[10, 10, 10]}
        angle={0.15}
        penumbra={1}
        intensity={1}
        castShadow
      />
      <spotLight
        position={[-10, -10, -10]}
        angle={0.15}
        penumbra={1}
        intensity={0.5}
      />
      <pointLight position={[0, 10, 0]} intensity={0.5} />
      
      <Environment preset="studio" />
      
      <Suspense fallback={<LoadingSpinner />}>
        <PresentationControls
          global
          snap
          rotation={[0, 0, 0]}
          polar={[-Math.PI / 4, Math.PI / 4]}
          azimuth={[-Math.PI / 4, Math.PI / 4]}
        >
          <Model url={modelUrl} autoRotate={autoRotate} />
        </PresentationControls>
      </Suspense>
      
      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={2}
        maxDistance={10}
        autoRotate={false}
      />
    </>
  );
}

export default function ModelViewer({ modelUrl, fallbackImage, productTitle }) {
  const [autoRotate, setAutoRotate] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Show fallback image if no model URL or error
  if (!modelUrl || hasError) {
    return (
      <div
        data-testid="model-viewer-fallback"
        className="relative w-full aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-zinc-900 to-black"
      >
        <img
          src={fallbackImage}
          alt={productTitle}
          className="w-full h-full object-cover"
        />
        <div className="absolute bottom-4 left-4 px-3 py-2 bg-black/60 backdrop-blur-sm rounded-lg">
          <span className="text-xs text-zinc-400">Image Preview</span>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="model-viewer"
      className={`relative w-full rounded-2xl overflow-hidden viewer-container ${
        isFullscreen ? 'fixed inset-0 z-50 rounded-none' : 'aspect-square'
      }`}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        shadows
        onError={() => setHasError(true)}
        gl={{ preserveDrawingBuffer: true, antialias: true }}
      >
        <Scene modelUrl={modelUrl} autoRotate={autoRotate} />
      </Canvas>

      {/* Controls */}
      <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setAutoRotate(!autoRotate)}
            data-testid="toggle-autorotate"
            className={`p-2 rounded-lg transition-all ${
              autoRotate
                ? 'bg-indigo-500 text-white'
                : 'bg-black/60 backdrop-blur-sm text-zinc-400 hover:text-white'
            }`}
            title={autoRotate ? 'Stop rotation' : 'Auto rotate'}
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center space-x-2">
          <span className="text-xs text-zinc-500 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-lg">
            Drag to rotate • Scroll to zoom
          </span>
          <button
            onClick={toggleFullscreen}
            data-testid="toggle-fullscreen"
            className="p-2 bg-black/60 backdrop-blur-sm text-zinc-400 hover:text-white rounded-lg transition-colors"
            title="Fullscreen"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* 3D Badge */}
      <div className="absolute top-4 left-4 px-3 py-1.5 bg-indigo-500/90 backdrop-blur-sm rounded-full flex items-center space-x-2">
        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
        <span className="text-xs font-medium text-white">Interactive 3D</span>
      </div>
    </div>
  );
}
